import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  TrendingUp,
  Calendar,
  Filter,
  Plus,
  CheckCircle,
  Clock,
  Target,
  Activity,
} from 'lucide-react';
import * as accountService from '../../services/accountService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Plan Dashboard Component
 * Main overview of all user programs with filters, summary cards, and quick actions
 */
export default function PlanDashboard() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user) {
      loadPrograms();
    }
  }, [user]);

  useEffect(() => {
    filterPrograms();
  }, [programs, typeFilter, statusFilter, searchTerm]);

  const loadPrograms = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError('');

    try {
      const result = await accountService.getUserPrograms(user.id);
      if (result.success) {
        setPrograms(result.data || []);
      } else {
        setError(result.error || 'Failed to load programs');
      }
    } catch (err) {
      console.error('Error loading programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterPrograms = () => {
    let filtered = [...programs];

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(p => p.program_type === typeFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.program_name?.toLowerCase().includes(term) ||
        p.program_type?.toLowerCase().includes(term)
      );
    }

    setFilteredPrograms(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'training_protocol':
        return Activity;
      case 'diet_plan':
        return Target;
      case 'coaching':
        return TrendingUp;
      default:
        return BookOpen;
    }
  };

  // Calculate summary statistics
  const activePrograms = programs.filter(p => p.status === 'active').length;
  const completedPrograms = programs.filter(p => p.status === 'completed').length;
  const totalPrograms = programs.length;

  // Calculate completion percentage
  const calculateCompletion = (program) => {
    if (!program.start_date || !program.end_date) return 0;
    const start = new Date(program.start_date);
    const end = new Date(program.end_date);
    const today = new Date();
    const total = end - start;
    const elapsed = today - start;
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  };

  // Calculate days remaining
  const calculateDaysRemaining = (program) => {
    if (!program.end_date) return null;
    const end = new Date(program.end_date);
    const today = new Date();
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-primary-600"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active Plans</h3>
            <Activity className="w-8 h-8 text-primary-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{activePrograms}</p>
          <p className="text-sm text-gray-500 mt-2">of {totalPrograms} total plans</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Completed</h3>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{completedPrograms}</p>
          <p className="text-sm text-gray-500 mt-2">Plans finished</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Total Plans</h3>
            <BookOpen className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalPrograms}</p>
          <p className="text-sm text-gray-500 mt-2">All programs</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search plans..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              aria-label="Search plans by name or type"
            />
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            aria-label="Filter plans by type"
          >
            <option value="all">All Types</option>
            <option value="coaching">Coaching</option>
            <option value="diet_plan">Diet Plan</option>
            <option value="training_protocol">Training Protocol</option>
            <option value="custom">Custom</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            aria-label="Filter plans by status"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Plans Grid */}
      {filteredPrograms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrograms.map((program) => {
            const TypeIcon = getTypeIcon(program.program_type);
            const completion = calculateCompletion(program);
            const daysRemaining = calculateDaysRemaining(program);

            return (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                role="article"
                aria-label={`Plan: ${program.program_name}`}
              >
                <Link 
                  to={`/account/programs/${program.id}`} 
                  className="block"
                  aria-label={`View details for ${program.program_name} plan`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                          <TypeIcon className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{program.program_name}</h3>
                          <p className="text-sm text-gray-500 capitalize">
                            {program.program_type.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(program.status)}`}>
                        {program.status}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    {program.status === 'active' && program.end_date && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span>Progress</span>
                          <span>{completion}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${completion}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Dates */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>Start: {new Date(program.start_date).toLocaleDateString()}</span>
                      </div>
                      {program.end_date && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Target className="w-4 h-4" />
                          <span>End: {new Date(program.end_date).toLocaleDateString()}</span>
                          {daysRemaining !== null && daysRemaining > 0 && (
                            <span className="text-primary-600 font-medium">
                              ({daysRemaining} days left)
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Coach Notes Preview */}
                    {program.coach_notes && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-700 mb-1">Coach Notes:</p>
                        <p className="text-sm text-gray-600 line-clamp-2">{program.coach_notes}</p>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="mt-4 flex gap-2">
                      <span className="text-xs text-primary-600 font-medium">View Details →</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No plans found</h3>
          <p className="text-gray-600">
            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : "You don't have any plans yet. Contact your coach to get started."}
          </p>
        </div>
      )}
    </div>
  );
}
